import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { NewsItem } from '../../../models/news';
import { NewsService } from '../../../services/news.service';

@Component({
  standalone: false,
  selector: 'app-news-admin',
  template: "<app-loading-overlay [show]=\"isLoading || isSaving || publishingNewsIds.size > 0 || deletingNewsIds.size > 0\"></app-loading-overlay>\n\n<details class=\"card accordion-card\" open>\n  <summary>\n    <span>Notizie</span>\n    <small>{{ news.length }} inserite</small>\n  </summary>\n\n  <div class=\"accordion-body\">\n    <div class=\"section-actions\">\n      <button type=\"button\" (click)=\"openCreateNewsModal()\" [disabled]=\"isSaving\">Aggiungi notizia</button>\n    </div>\n\n    <p class=\"error-message\" *ngIf=\"errorMessage\">{{ errorMessage }}</p>\n    <p class=\"success-message\" *ngIf=\"successMessage\">{{ successMessage }}</p>\n    <p class=\"loading-message\" *ngIf=\"isSaving || publishingNewsIds.size || deletingNewsIds.size\">Operazione in corso, attendere...</p>\n\n    <div class=\"news-list\">\n      <div *ngFor=\"let item of news\" class=\"list-item\">\n        <div class=\"news-identity\">\n          <img [src]=\"newsPhoto(item)\" [alt]=\"item.title\" />\n          <div class=\"item-main\">\n            <strong>{{ item.title }}</strong>\n            <small>{{ item.description }}</small>\n            <span>{{ isPublished(item) ? 'Pubblicata' : 'Bozza' }} - {{ item.created_at | date: 'd MMMM y, HH:mm' }}</span>\n          </div>\n        </div>\n        <div class=\"list-actions\">\n          <button class=\"secondary\" (click)=\"togglePublish(item)\" [disabled]=\"isPublishingNews(item.id)\">\n            {{ isPublishingNews(item.id) ? 'Attendere...' : (isPublished(item) ? 'Nascondi' : 'Pubblica') }}\n          </button>\n          <button class=\"secondary\" (click)=\"editNews(item)\" [disabled]=\"isSaving || isDeletingNews(item.id)\">Modifica</button>\n          <button class=\"danger\" (click)=\"deleteNews(item)\" [disabled]=\"isDeletingNews(item.id)\">\n            {{ isDeletingNews(item.id) ? 'Cancellazione...' : 'Elimina' }}\n          </button>\n        </div>\n      </div>\n    </div>\n  </div>\n</details>\n\n<div class=\"modal-backdrop\" *ngIf=\"newsModalOpen\">\n  <section class=\"modal-panel\">\n    <div class=\"modal-heading\">\n      <h2>{{ editingNews ? 'Modifica notizia' : 'Nuova notizia' }}</h2>\n      <button type=\"button\" class=\"secondary icon-button\" (click)=\"closeNewsModal()\">Chiudi</button>\n    </div>\n\n    <form class=\"form-grid\" (submit)=\"$event.preventDefault(); saveNews();\">\n      <label>\n        Titolo\n        <input type=\"text\" placeholder=\"Titolo notizia\" [(ngModel)]=\"newsForm.title\" name=\"newsTitle\" />\n      </label>\n\n      <label>\n        Descrizione\n        <textarea rows=\"4\" placeholder=\"Descrizione notizia\" [(ngModel)]=\"newsForm.description\" name=\"newsDescription\"></textarea>\n      </label>\n\n      <label class=\"checkbox-field\">\n        <input type=\"checkbox\" [(ngModel)]=\"newsForm.is_published\" name=\"newsPublished\" />\n        Pubblicata\n      </label>\n\n      <label>\n        Foto <span class=\"optional-label\">(opzionale)</span>\n        <input type=\"file\" accept=\"image/*\" (change)=\"loadPhoto($event)\" />\n      </label>\n\n      <div class=\"photo-preview\" *ngIf=\"newsForm.photo\">\n        <img [src]=\"newsForm.photo\" alt=\"Anteprima notizia\" />\n        <button type=\"button\" class=\"secondary\" (click)=\"removePhoto()\">Rimuovi foto</button>\n      </div>\n\n      <p class=\"error-message\" *ngIf=\"errorMessage\">{{ errorMessage }}</p>\n      <p class=\"loading-message\" *ngIf=\"isSaving\">Salvataggio in corso...</p>\n\n      <div class=\"actions\">\n        <button type=\"submit\" [disabled]=\"isSaving\">{{ isSaving ? 'Salvataggio...' : (editingNews ? 'Aggiorna notizia' : 'Aggiungi notizia') }}</button>\n        <button type=\"button\" class=\"secondary\" (click)=\"closeNewsModal()\" [disabled]=\"isSaving\">Annulla</button>\n      </div>\n    </form>\n  </section>\n</div>\n",
  styles: [".news-list {\n  display: grid;\n  gap: 0.75rem;\n  margin-top: 1rem;\n}\n\n.news-identity {\n  align-items: center;\n  display: flex;\n  gap: 0.75rem;\n  min-width: 0;\n}\n\n.news-identity img {\n  background: #f8fafc;\n  border: 1px solid rgba(255, 255, 255, 0.08);\n  border-radius: 8px;\n  height: 56px;\n  object-fit: cover;\n  width: 80px;\n}\n\n.optional-label {\n  color: #94a3b8;\n  font-size: 0.85rem;\n  font-weight: 600;\n}\n\n.checkbox-field {\n  align-items: center;\n  display: flex;\n  flex-direction: row;\n  gap: 0.55rem;\n}\n\n.checkbox-field input {\n  width: auto;\n}\n\n.photo-preview {\n  align-items: center;\n  display: flex;\n  gap: 0.75rem;\n}\n\n.photo-preview img {\n  border-radius: 8px;\n  height: 110px;\n  object-fit: cover;\n  width: 160px;\n}\n\n.actions {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.6rem;\n}\n"]
})
export class NewsAdminComponent implements OnInit {
  readonly fallbackPhoto = '/assets/img/LogoScipioneCup.png';

  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly newsService = new NewsService();

  news: NewsItem[] = [];
  newsForm: Partial<NewsItem> = { title: '', description: '', photo: '', is_published: false };
  editingNews: NewsItem | null = null;
  newsModalOpen = false;
  errorMessage = '';
  successMessage = '';
  isLoading = true;
  isSaving = false;
  publishingNewsIds = new Set<number>();
  deletingNewsIds = new Set<number>();

  ngOnInit(): void {
    this.loadNews();
  }

  async loadNews(): Promise<void> {
    try {
      this.isLoading = true;
      this.changeDetector.detectChanges();
      this.news = await this.newsService.listAdmin();
      this.errorMessage = '';
    } catch (error) {
      this.errorMessage = this.readError(error, 'Errore durante il caricamento delle notizie.');
    } finally {
      this.isLoading = false;
      this.changeDetector.detectChanges();
    }
  }

  async saveNews(): Promise<void> {
    if (this.isSaving) {
      return;
    }

    const title = this.newsForm.title?.trim() ?? '';
    const description = this.newsForm.description?.trim() ?? '';
    const photo = this.newsForm.photo?.trim() ?? '';
    if (!title) {
      this.errorMessage = 'Inserisci il titolo della notizia.';
      return;
    }
    if (!description) {
      this.errorMessage = 'Inserisci la descrizione della notizia.';
      return;
    }
    const payload = {
      title,
      description,
      photo,
      is_published: Boolean(this.newsForm.is_published)
    };

    try {
      this.isSaving = true;
      this.errorMessage = '';
      this.successMessage = '';
      if (this.editingNews) {
        await this.newsService.update(this.editingNews.id, payload);
      } else {
        await this.newsService.create(payload);
      }

      this.newsModalOpen = false;
      this.successMessage = 'Operazione conclusa. Notizia salvata.';
      this.resetNewsForm();
      await this.loadNews();
    } catch (error) {
      this.successMessage = '';
      this.errorMessage = this.readError(error, 'Errore durante il salvataggio della notizia.');
      this.changeDetector.detectChanges();
    } finally {
      this.isSaving = false;
      this.changeDetector.detectChanges();
    }
  }

  editNews(news: NewsItem): void {
    this.editingNews = news;
    this.newsForm = {
      title: news.title,
      description: news.description,
      photo: news.photo,
      is_published: Boolean(news.is_published)
    };
    this.newsModalOpen = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.changeDetector.detectChanges();
  }

  async togglePublish(news: NewsItem): Promise<void> {
    if (this.publishingNewsIds.has(news.id)) {
      return;
    }

    try {
      this.publishingNewsIds.add(news.id);
      this.errorMessage = '';
      this.successMessage = '';
      if (this.isPublished(news)) {
        await this.newsService.unpublish(news.id);
      } else {
        await this.newsService.publish(news.id);
      }
      this.successMessage = 'Operazione conclusa. Stato pubblicazione aggiornato.';
      await this.loadNews();
    } catch (error) {
      this.successMessage = '';
      this.errorMessage = this.readError(error, 'Errore durante la pubblicazione della notizia.');
      this.changeDetector.detectChanges();
    } finally {
      this.publishingNewsIds.delete(news.id);
      this.changeDetector.detectChanges();
    }
  }

  async deleteNews(news: NewsItem): Promise<void> {
    if (this.deletingNewsIds.has(news.id)) {
      return;
    }

    if (!window.confirm(`Confermi la cancellazione della notizia "${news.title}"?`)) {
      return;
    }

    try {
      this.deletingNewsIds.add(news.id);
      this.errorMessage = '';
      this.successMessage = '';
      await this.newsService.delete(news.id);
      this.news = this.news.filter((item) => item.id !== news.id);
      this.successMessage = 'Operazione conclusa. Notizia cancellata.';
      await this.loadNews();
    } catch (error) {
      this.successMessage = '';
      this.errorMessage = this.readError(error, 'Errore durante la cancellazione della notizia.');
      this.changeDetector.detectChanges();
    } finally {
      this.deletingNewsIds.delete(news.id);
      this.changeDetector.detectChanges();
    }
  }

  openCreateNewsModal(): void {
    this.resetNewsForm();
    this.newsModalOpen = true;
    this.successMessage = '';
    this.changeDetector.detectChanges();
  }

  closeNewsModal(): void {
    this.newsModalOpen = false;
    this.resetNewsForm();
  }

  loadPhoto(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.newsForm.photo = reader.result as string;
      this.changeDetector.detectChanges();
    };
    reader.readAsDataURL(file);
  }

  removePhoto(): void {
    this.newsForm.photo = '';
    this.changeDetector.detectChanges();
  }

  newsPhoto(news: Pick<NewsItem, 'photo'>): string {
    return news.photo?.trim() || this.fallbackPhoto;
  }

  isPublished(news: NewsItem): boolean {
    return news.is_published === true || Number(news.is_published) === 1;
  }

  isPublishingNews(newsId: number): boolean {
    return this.publishingNewsIds.has(newsId);
  }

  isDeletingNews(newsId: number): boolean {
    return this.deletingNewsIds.has(newsId);
  }

  resetNewsForm(): void {
    this.editingNews = null;
    this.newsForm = { title: '', description: '', photo: '', is_published: false };
    this.errorMessage = '';
    this.changeDetector.detectChanges();
  }

  private readError(error: unknown, fallback: string): string {
    return error instanceof Error ? error.message : fallback;
  }
}
